import { Building2, Stethoscope, Activity, Users, Clock, Phone, ChevronRight, Home, ShieldCheck, MapPin, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Hospital = () => {
  const facilities = [
    {
      icon: Stethoscope,
      title: 'Advanced Operation Theatres',
      description: 'State-of-the-art modular OTs equipped with latest surgical equipment and laminar flow technology',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'ICU & Critical Care',
      description: '24/7 intensive care unit with advanced monitoring systems and experienced critical care staff',
      color: 'from-teal-500 to-emerald-600'
    },
    {
      icon: Users,
      title: 'Modern Diagnostic Center',
      description: 'Complete pathology lab, digital X-ray, ultrasound, CT scan, and other diagnostic facilities',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Clock,
      title: '24/7 Emergency Services',
      description: 'Round-the-clock emergency care with dedicated trauma team and ambulance services',
      color: 'from-rose-500 to-red-600'
    },
  ];

  const departments = [
    'Urology & Kidney Transplant', 'General Surgery', 'Orthopedics', 'Gynecology',
    'Pediatrics', 'Internal Medicine', 'Cardiology', 'ENT',
  ];

  return (
    <div className="pt-20 bg-[#f8fafc] overflow-hidden">
      
      {/* --- 1. PREMIUM BREADCRUMB HEADER (Consistent Design) --- */}
      <section className="bg-gradient-to-br from-blue-900 to-teal-800 py-12 relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-teal-400/10 rounded-full -ml-10 -mb-10 blur-2xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-teal-200/80 text-sm font-bold uppercase tracking-widest mb-4">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" /> Home
            </Link>
            <ChevronRight className="w-4 h-4 opacity-50" />
            <span className="text-white">BBMH Hospital</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">Our Medical Facility</h1>
          <div className="h-1.5 w-24 bg-teal-400 mt-4 rounded-full" />
          <p className="text-blue-100/70 mt-6 max-w-2xl font-medium text-lg italic">
            Brijesh Banger Memorial Hospital — A premier multi-specialty healthcare facility in Bhilwara, Rajasthan.
          </p>
        </div>
      </section>

      {/* --- 2. ABOUT OVERVIEW (Glassmorphic Style) --- */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5 relative">
              <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white">
                {/* Decorative Icon Box */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center">
                   <Building2 className="w-32 h-32 text-blue-900 opacity-20" />
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-teal-400 rounded-full -z-10 animate-pulse" />
            </div>
            
            <div className="lg:col-span-7">
              <h2 className="text-4xl font-black text-slate-900 mb-8 italic">About BBMH</h2>
              <div className="space-y-6 text-slate-600 font-medium leading-relaxed">
                <p className="text-xl text-slate-900 font-bold">
                  Brijesh Banger Memorial Hospital (BBMH) stands as a beacon of excellence in healthcare in Bhilwara, Rajasthan.
                </p>
                <p>
                  Established with a vision to provide world-class medical services, BBMH has grown to become one of the most trusted healthcare institutions in the region. The hospital is equipped with cutting-edge medical technology and staffed by highly qualified medical professionals across various specialties.
                </p>
                <p className="bg-blue-50 p-6 rounded-3xl border-l-4 border-blue-600 italic">
                  With a focus on advanced surgical procedures, particularly in urology and kidney transplantation under the leadership of Dr. Sachitanand Chaurasia, BBMH has successfully treated thousands of patients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 3. INFRASTRUCTURE GRID (Modern Cards) --- */}
      <section className="py-24 bg-slate-900 text-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4 italic">Infrastructure & Facilities</h2>
            <p className="text-blue-300/60 font-black uppercase tracking-widest text-xs">Modern healthcare infrastructure</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {facilities.map((facility, index) => (
              <motion.div 
                whileHover={{ y: -10 }}
                key={index} 
                className="bg-white/5 backdrop-blur-md p-10 rounded-[3rem] border border-white/10 group hover:bg-white hover:text-slate-900 transition-all duration-500"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${facility.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                  <facility.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-black mb-4">{facility.title}</h3>
                <p className="opacity-60 group-hover:opacity-100 font-medium leading-relaxed">
                  {facility.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 4. OT STANDARDS (Clean White Cards) --- */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <ShieldCheck className="w-12 h-12 text-teal-500 mx-auto mb-4" />
            <h2 className="text-4xl font-black text-slate-900 italic">Operation Theatre Standards</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100">
              <h3 className="text-xl font-black text-blue-900 mb-6 flex items-center gap-2">
                <Zap className="w-5 h-5" /> Modular OT Features
              </h3>
              <ul className="space-y-4">
                {[
                  'Laminar air flow system for infection control',
                  'Advanced laparoscopic equipment',
                  'C-arm and fluoroscopy facilities',
                  'State-of-the-art anesthesia workstations'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600 font-bold text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100">
              <h3 className="text-xl font-black text-blue-900 mb-6 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" /> Safety & Hygiene
              </h3>
              <ul className="space-y-4">
                {[
                  'Strict infection control protocols',
                  'Regular sterilization and maintenance',
                  'Dedicated surgical team trained in protocols',
                  'Quality assurance and monitoring systems'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600 font-bold text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- 5. DEPARTMENTS (Capsule Style) --- */}
      <section className="py-20 bg-blue-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-black text-slate-900 mb-12">All Medical Departments</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {departments.map((dept, index) => (
              <span key={index} className="px-8 py-4 bg-white border border-slate-200 rounded-2xl font-black text-slate-600 text-sm hover:shadow-lg hover:border-blue-400 transition-all cursor-default">
                {dept}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* --- 6. LOCATION & CONTACT (Card Style) --- */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-[4rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row">
            <div className="lg:w-1/2 p-12 md:p-16 text-white">
              <h2 className="text-3xl font-black mb-8 italic">Location & Contact</h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <MapPin className="text-teal-400 shrink-0" />
                  <div>
                    <p className="font-black text-lg">Hospital Address</p>
                    <p className="text-slate-400 font-medium">Brijesh Banger Memorial Hospital, Bhilwara, Rajasthan, 311001</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone className="text-teal-400 shrink-0" />
                  <div>
                    <p className="font-black text-lg">General Enquiries</p>
                    <p className="text-slate-400 font-medium">+91 1482 245678</p>
                  </div>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-3xl">
                  <p className="text-red-400 font-black text-xs uppercase tracking-widest mb-2">Emergency Helpline</p>
                  <p className="text-2xl font-black text-white">+91 1482 245679</p>
                </div>
              </div>
            </div>
            
            <div className="lg:w-1/2 bg-slate-800 flex items-center justify-center p-12 relative">
               {/* This is where Google Maps iframe would go */}
               <div className="text-center text-slate-500">
                  <MapPin className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p className="font-bold">Google Maps Integration</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 7. FINAL CTA --- */}
      <section className="py-20 mb-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
           <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-1 rounded-[4rem]">
             <div className="bg-white rounded-[3.9rem] p-16">
                <h2 className="text-4xl font-black text-slate-900 mb-6 italic">Need Expert Medical Care?</h2>
                <p className="text-slate-500 text-lg font-bold mb-10 max-w-xl mx-auto">
                  Experience world-class healthcare with top-tier medical experts at BBMH.
                </p>
                <Link to="/appointment" className="bg-slate-900 text-white px-12 py-5 rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-xl inline-block">
                  Book Your Appointment
                </Link>
             </div>
           </div>
        </div>
      </section>

    </div>
  );
};

export default Hospital;